(() => {
  const ITEM_SELECTOR = '.menu-list__list-item:not([slot="overflow"])';
  const LINK_SELECTOR = ':scope > .menu-list__link[aria-haspopup="true"]';
  const SUBMENU_SELECTOR = ':scope > .menu-list__submenu';

  const alignItem = (item) => {
    const link = item.querySelector(LINK_SELECTOR);
    const submenu = item.querySelector(SUBMENU_SELECTOR);
    if (!link || !submenu) return;

    const edgeGap = 24;
    const linkLeft = link.getBoundingClientRect().left;
    const submenuWidth = submenu.getBoundingClientRect().width || submenu.scrollWidth;
    const maximumLeft = Math.max(edgeGap, window.innerWidth - submenuWidth - edgeGap);
    const safeLeft = Math.round(Math.min(Math.max(edgeGap, linkLeft), maximumLeft));
    submenu.style.setProperty('--tcg-submenu-parent-left', `${safeLeft}px`);
  };

  const prepareItem = (item) => {
    if (item.dataset.tcgSubmenuAligned === 'true') return;
    if (!item.querySelector(LINK_SELECTOR) || !item.querySelector(SUBMENU_SELECTOR)) return;

    item.dataset.tcgSubmenuAligned = 'true';
    item.addEventListener('pointerenter', () => alignItem(item));
    item.addEventListener('focusin', () => alignItem(item));
    item.addEventListener('click', () => alignItem(item));
    alignItem(item);
  };

  const prepareMenus = () => {
    document.querySelectorAll(`#header-component ${ITEM_SELECTOR}`).forEach(prepareItem);
  };

  const realignOpenMenus = () => {
    document
      .querySelectorAll(`#header-component ${ITEM_SELECTOR}:has(> .menu-list__link[aria-expanded="true"])`)
      .forEach(alignItem);
  };

  prepareMenus();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepareMenus, { once: true });
  }
  document.addEventListener('shopify:section:load', prepareMenus);
  window.addEventListener('resize', realignOpenMenus, { passive: true });
})();

// Storefront-wide rapid cart guard. This lives in a global asset so it is not
// held behind Shopify's rendered-section cache. Rapid clicks are grouped into
// one Shopify request, while each product card gets immediate visual feedback.
(() => {
  if (window.__tcgRapidCartGuardInstalled) return;
  window.__tcgRapidCartGuardInstalled = true;

  const FORM_SELECTOR = [
    '.tcg-featured-card__form',
    '.tcg-card__form',
    '[data-sealed-card-form]',
  ].join(',');
  const BATCH_IDLE_MS = 180;
  const BATCH_MAX_WAIT_MS = 500;
  const CONFIRMATION_MS = 1400;
  const rapidCart = window.__tcgManaVaultRapidCart || {
    pending: [],
    idleTimer: 0,
    maxTimer: 0,
    requestTail: Promise.resolve(),
  };
  window.__tcgManaVaultRapidCart = rapidCart;

  const stateStyle = document.createElement('style');
  stateStyle.dataset.tcgRapidCartStyles = 'true';
  stateStyle.textContent = `
    [data-tcg-cart-state="added"] {
      background: #15803d !important;
      border-color: #15803d !important;
      color: #fff !important;
      opacity: 1 !important;
    }
    [data-tcg-cart-state="error"] {
      background: #b91c1c !important;
      border-color: #b91c1c !important;
      color: #fff !important;
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(stateStyle);

  const fetchCart = async () => {
    const rootPath = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${rootPath}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Unable to confirm the cart.');
    return response.json();
  };

  const dispatchFinalCart = (cart, sections = {}) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(cart.item_count);
    });

    const updateEvent = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    updateEvent.promise = Promise.resolve({
      cart: {
        totalQuantity: cart.item_count,
        lines: cart.items,
      },
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'tcg-rapid-cart-guard',
        didError: false,
      },
    });
    document.dispatchEvent(updateEvent);
  };

  const readResponse = async (response) => {
    const text = await response.text();
    let result = {};
    try {
      result = text ? JSON.parse(text) : {};
    } catch (parseError) {
      if (!response.ok) throw new Error('Shopify returned an unexpected response. Please try again.');
    }
    if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
    return result;
  };

  const cartSectionIds = () => Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
    .filter(Boolean);

  const postItems = async (items) => {
    const rootPath = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${rootPath}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    return readResponse(response);
  };

  const clearBatchTimers = () => {
    window.clearTimeout(rapidCart.idleTimer);
    window.clearTimeout(rapidCart.maxTimer);
    rapidCart.idleTimer = 0;
    rapidCart.maxTimer = 0;
  };

  const flushBatch = () => {
    if (!rapidCart.pending.length) return;
    clearBatchTimers();
    const entries = rapidCart.pending.splice(0);
    const grouped = new Map();
    entries.forEach((entry) => {
      const current = grouped.get(entry.variantId) || 0;
      grouped.set(entry.variantId, current + entry.quantity);
    });
    const items = Array.from(grouped, ([id, quantity]) => ({ id: Number(id), quantity }));

    const request = rapidCart.requestTail
      .catch(() => undefined)
      .then(async () => {
        try {
          const result = await postItems(items);
          const cart = await fetchCart();
          dispatchFinalCart(cart, result.sections || {});
          entries.forEach((entry) => entry.resolve());
        } catch (batchError) {
          // A single unavailable variant should not prevent the other rapid
          // clicks from being added. Retry individually only on this rare path.
          for (const entry of entries) {
            try {
              await postItems([{ id: Number(entry.variantId), quantity: entry.quantity }]);
              entry.resolve();
            } catch (error) {
              entry.reject(error);
            }
          }
          try {
            dispatchFinalCart(await fetchCart());
          } catch (cartError) {
            console.warn('[tcg-rapid-cart-guard] Final cart refresh failed.', cartError);
          }
        }
      });
    rapidCart.requestTail = request;
  };

  const enqueue = (entry) => new Promise((resolve, reject) => {
    rapidCart.pending.push({ ...entry, resolve, reject });
    window.clearTimeout(rapidCart.idleTimer);
    rapidCart.idleTimer = window.setTimeout(flushBatch, BATCH_IDLE_MS);
    if (!rapidCart.maxTimer) {
      rapidCart.maxTimer = window.setTimeout(flushBatch, BATCH_MAX_WAIT_MS);
    }
  });

  const formControls = (form) => {
    if (form.matches('.tcg-featured-card__form')) {
      const card = form.closest('.tcg-featured-card');
      return {
        addButton: form.querySelector('[data-featured-add]'),
        quantityInput: form.querySelector('[data-featured-quantity]'),
        variantInput: form.querySelector('[data-featured-variant], [data-featured-variant-input]'),
        message: card?.querySelector('[data-featured-message]'),
        messageIcon: card?.querySelector('[data-featured-message-icon]'),
        messageText: card?.querySelector('[data-featured-message-text]'),
      };
    }
    if (form.matches('.tcg-card__form')) {
      const card = form.closest('.tcg-card');
      return {
        addButton: form.querySelector('[data-card-add]'),
        quantityInput: form.querySelector('[data-card-quantity]'),
        variantInput: form.querySelector('[data-card-variant], [data-card-variant-input]'),
        message: card?.querySelector('[data-card-message]'),
        messageIcon: card?.querySelector('[data-card-message-icon]'),
        messageText: card?.querySelector('[data-card-message-text]'),
      };
    }
    return {
      addButton: form.querySelector('[data-sealed-add]'),
      quantityInput: form.querySelector('[name="quantity"]'),
      variantInput: form.querySelector('[data-sealed-variant], [data-sealed-variant-input], [name="id"]'),
      message: form.querySelector('[data-sealed-message]'),
      messageIcon: form.querySelector('[data-sealed-message-icon]'),
      messageText: form.querySelector('[data-sealed-message-text]'),
    };
  };

  const showAddedState = (button) => {
    button.dataset.tcgCartState = 'added';
    button.textContent = 'Added to cart';
  };

  const restoreButton = (button) => {
    window.setTimeout(() => {
      if (button.dataset.tcgCartState !== 'added') return;
      delete button.dataset.tcgCartState;
      button.textContent = 'Add to cart';
    }, CONFIRMATION_MS);
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target instanceof Element ? event.target.closest(FORM_SELECTOR) : null;
      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const { addButton, quantityInput, variantInput, message, messageIcon, messageText } = formControls(form);
      if (!addButton || !variantInput || addButton.disabled) return;

      const variantId = variantInput.value;
      const requestedQuantity = Number(quantityInput?.value || 1);
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      showAddedState(addButton);
      if (message) {
        message.hidden = true;
        message.classList.remove('is-error');
      }

      try {
        await enqueue({ variantId, quantity: requestedQuantity });
        restoreButton(addButton);
      } catch (error) {
        addButton.dataset.tcgCartState = 'error';
        addButton.textContent = 'Try again';
        if (messageText) messageText.textContent = error.message || 'Unable to add this item.';
        if (messageIcon) messageIcon.textContent = '×';
        if (message) {
          message.classList.add('is-error');
          message.hidden = false;
        }
      } finally {
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
      }
    },
    true
  );
})();

